import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo } from "react";
import { PageHero } from "@/components/site/PageHero";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { activeItems, galleryItemsQueryOptions } from "@/lib/gallery.queries";
import {
  EmptyState,
  FeaturedVideo,
  PhotoGrid,
  SectionHeading,
  VideoGrid,
} from "@/components/site/GallerySections";

type GalleryTab = "videos" | "photos";

type GallerySearch = {
  /** Legacy miinews.com deep link: /gallery?video=<permalink>. */
  video?: string;
  tab?: GalleryTab;
  /** Site-wide design-direction override (?v=v1..v4) — kept so shared links still work. */
  v?: string;
};

export const Route = createFileRoute("/gallery")({
  validateSearch: (search: Record<string, unknown>): GallerySearch => {
    const video = typeof search.video === "string" && search.video ? search.video : undefined;
    const tab = search.tab === "photos" ? "photos" : search.tab === "videos" ? "videos" : undefined;
    const v = typeof search.v === "string" && search.v ? search.v : undefined;
    return { video, tab, v };
  },
  beforeLoad: ({ search }) => {
    if (search.video) {
      throw redirect({
        to: "/gallery/$permalink",
        params: { permalink: search.video },
        search: { ...search, video: undefined },
        replace: true,
      });
    }
  },
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(galleryItemsQueryOptions("gallery-video")),
      context.queryClient.ensureQueryData(galleryItemsQueryOptions("gallery-photo")),
    ]);
  },
  head: () => ({
    meta: [
      { title: "Gallery — Medical Insight" },
      {
        name: "description",
        content:
          "Video and photo highlights from Medical Insight and the Aesthetic Industry Association — meetings, awards, and industry coverage.",
      },
      { property: "og:title", content: "Gallery — Medical Insight" },
    ],
  }),
  component: GalleryPage,
});

function GalleryPage() {
  return (
    <Suspense
      fallback={
        <div className="mx-auto max-w-7xl px-4 py-16 text-sm text-muted-foreground">
          Loading gallery…
        </div>
      }
    >
      <GalleryContent />
    </Suspense>
  );
}

function GalleryContent() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { data: rawVideos } = useSuspenseQuery(galleryItemsQueryOptions("gallery-video"));
  const { data: rawPhotos } = useSuspenseQuery(galleryItemsQueryOptions("gallery-photo"));

  const videos = useMemo(() => activeItems(rawVideos), [rawVideos]);
  const photos = useMemo(() => activeItems(rawPhotos), [rawPhotos]);
  const [featured] = videos;

  const activeTab: GalleryTab = tab === "photos" ? "photos" : "videos";

  return (
    <div className="bg-background">
      <PageHero
        page="gallery"
        kicker="Medical Insight"
        title="Gallery"
        subtitle="Video and photo highlights from our meetings, awards programs, and industry coverage."
      />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:py-14">
        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            navigate({
              to: "/gallery",
              search: (prev) => ({ ...prev, tab: value === "photos" ? "photos" : undefined }),
              replace: true,
            })
          }
        >
          <TabsList>
            <TabsTrigger value="videos">
              Videos
            </TabsTrigger>
            <TabsTrigger value="photos">
              Photos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="videos" className="mt-8">
            {videos.length === 0 ? (
              <EmptyState
                title="No videos yet"
                message="Gallery videos are on their way. Check back soon."
              />
            ) : (
              <>
                {featured && (
                  <section className="mb-12">
                    <FeaturedVideo item={featured} basePath="/gallery" />
                  </section>
                )}
                <section>
                  <SectionHeading title="All videos" />
                  <VideoGrid items={videos} basePath="/gallery" />
                </section>
              </>
            )}
          </TabsContent>

          <TabsContent value="photos" className="mt-8">
            {photos.length === 0 ? (
              <EmptyState
                icon="photo"
                title="No photos yet"
                message="Event photography will appear here once it has been published."
              />
            ) : (
              <section>
                <SectionHeading title="Photos" />
                <PhotoGrid items={photos} />
              </section>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* /gallery/$permalink renders the video lightbox over this page. */}
      <Outlet />
    </div>
  );
}
